import { useState, useCallback, useMemo } from 'react';

/**
 * useFrameSession - Hook for managing frame-specific session state
 *
 * This hook manages ephemeral state that exists only during the current session,
 * not persisted to Firebase. Used for UI state like cooking steps, dice pools,
 * matched sets, active modals, etc.
 *
 * @param {string} frameId - The frame identifier to namespace the state
 * @returns {object} Session state and update functions
 *
 * @example
 * const {
 *   sessionState,
 *   updateSessionState,
 *   resetCookingState,
 *   clearFrameSession
 * } = useFrameSession('beast-feast');
 *
 * // Update single field
 * updateSessionState({ cookingStep: 'rolling' });
 *
 * // Update with function
 * updateSessionState(prev => ({ ...prev, mealRating: prev.mealRating + 5 }));
 */
/**
 * Returns initial session state for a given frame.
 * Known SRD frames get typed initial state.
 * Unknown/homebrew frames get common state only — patterns manage their own keys via updateSessionState.
 */
const getInitialState = (frameId) => {
  // Common state shared by all frames
  const common = {
    activeModals: [],
    lastError: null,
    isProcessing: false,
  };

  switch (frameId) {
    case 'beast-feast':
      return {
        ...common,
        cookingStep: 'prepare',
        selectedIngredients: [],
        dicePool: [],
        matchedSets: [],
        mealRating: 0,
        recipeTokens: 0,
        hpToHeal: 0,
        stressToClear: 0,
        hopeToGain: 0,
        scarsToClear: 0,
        recipeName: '',
        recipeDescription: '',
        showCookbook: false,
        animationIngredients: [],
        isStirring: false,
        isGeneratingAI: false,
      };

    case 'witherwild':
      return {
        ...common,
        witherTokensOnCharacter: {},
        currentPhase: 'day',
        diseaseStages: {},
      };

    case 'five-banners-burning':
      return {
        ...common,
        expandedNation: null,
      };

    case 'age-of-umbra':
      return {
        ...common,
        lastLurkingDarknessRoll: null,
        keepWatchRolls: [],
      };

    case 'motherboard':
      return {
        ...common,
        scrapRollResults: [],
        shopStock: null,
        selectedScrapCategory: null,
      };

    case 'colossus-drylands':
      return {
        ...common,
        colossusSetupSegments: [],
        siphonTrackMarks: 1,
        kudamatPower: 0,
      };

    default:
      // Homebrew/unknown frames: patterns add keys as needed
      return { ...common };
  }
};

const useFrameSession = (frameId) => {
  const initialState = useMemo(() => getInitialState(frameId), [frameId]);

  const [sessionState, setSessionState] = useState(initialState);

  /**
   * Update session state with partial updates or a function
   * @param {object|function} updates - Partial state object or updater function
   */
  const updateSessionState = useCallback((updates) => {
    setSessionState(prev => ({
      ...prev,
      ...(typeof updates === 'function' ? updates(prev) : updates),
    }));
  }, []);

  /**
   * Reset all session state to initial values
   */
  const clearFrameSession = useCallback(() => {
    setSessionState(initialState);
  }, [initialState]);

  // ---------------------------------------------------------------------------
  // Beast Feast Specific Helpers
  // ---------------------------------------------------------------------------

  /**
   * Reset cooking state for a new cooking session
   */
  const resetCookingState = useCallback(() => {
    setSessionState(prev => ({
      ...prev,
      cookingStep: 'prepare',
      selectedIngredients: [],
      dicePool: [],
      matchedSets: [],
      mealRating: 0,
      recipeTokens: 0,
      hpToHeal: 0,
      stressToClear: 0,
      hopeToGain: 0,
      scarsToClear: 0,
      recipeName: '',
      recipeDescription: '',
      animationIngredients: [],
      isStirring: false,
      isGeneratingAI: false,
    }));
  }, []);

  /**
   * Toggle ingredient selection
   * @param {string} ingredientId - The ingredient ID to toggle
   * @param {object} ingredient - The full ingredient object (for animation)
   */
  const toggleIngredient = useCallback((ingredientId, ingredient) => {
    setSessionState(prev => {
      const isSelected = prev.selectedIngredients.includes(ingredientId);

      if (isSelected) {
        // Removing ingredient
        return {
          ...prev,
          selectedIngredients: prev.selectedIngredients.filter(id => id !== ingredientId),
          animationIngredients: prev.animationIngredients.filter(ing => ing.id !== ingredientId),
        };
      } else {
        // Adding ingredient
        return {
          ...prev,
          selectedIngredients: [...prev.selectedIngredients, ingredientId],
          animationIngredients: ingredient
            ? [...prev.animationIngredients, ingredient]
            : prev.animationIngredients,
        };
      }
    });
  }, []);

  /**
   * Add dice to the pool
   * @param {object[]} dice - Array of dice objects { id, sides, value, ingredientName }
   */
  const addDiceToPool = useCallback((dice) => {
    setSessionState(prev => ({
      ...prev,
      dicePool: [...prev.dicePool, ...dice],
    }));
  }, []);

  /**
   * Update dice pool after rolling
   * @param {object[]} newPool - The new dice pool
   * @param {object[]} newMatches - Any new matched dice
   */
  const updateDicePool = useCallback((newPool, newMatches = []) => {
    setSessionState(prev => ({
      ...prev,
      dicePool: newPool,
      matchedSets: [...prev.matchedSets, ...newMatches],
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Witherwild Specific Helpers
  // ---------------------------------------------------------------------------

  /**
   * Add wither tokens to a character
   * @param {string} characterId - The character ID
   * @param {number} amount - Number of tokens to add (default 1)
   */
  const addWitherTokens = useCallback((characterId, amount = 1) => {
    setSessionState(prev => ({
      ...prev,
      witherTokensOnCharacter: {
        ...prev.witherTokensOnCharacter,
        [characterId]: (prev.witherTokensOnCharacter[characterId] || 0) + amount,
      },
    }));
  }, []);

  /**
   * Clear wither tokens for a character
   * @param {string} characterId - The character ID
   */
  const clearWitherTokens = useCallback((characterId) => {
    setSessionState(prev => {
      const { [characterId]: removed, ...rest } = prev.witherTokensOnCharacter;
      return {
        ...prev,
        witherTokensOnCharacter: rest,
      };
    });
  }, []);

  /**
   * Toggle day/night phase
   */
  const togglePhase = useCallback(() => {
    setSessionState(prev => ({
      ...prev,
      currentPhase: prev.currentPhase === 'day' ? 'night' : 'day',
    }));
  }, []);

  /**
   * Update disease stage for a character
   * @param {string} characterId - The character ID
   * @param {string} stageId - The new stage ID
   */
  const updateDiseaseStage = useCallback((characterId, stageId) => {
    setSessionState(prev => ({
      ...prev,
      diseaseStages: {
        ...prev.diseaseStages,
        [characterId]: stageId,
      },
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Modal Helpers
  // ---------------------------------------------------------------------------

  /**
   * Push a modal onto the active modals stack
   * @param {string} modalId - The modal identifier
   */
  const openModal = useCallback((modalId) => {
    setSessionState(prev => ({
      ...prev,
      activeModals: [...prev.activeModals, modalId],
    }));
  }, []);

  /**
   * Pop a modal from the active modals stack
   * @param {string} modalId - Optional specific modal to close (defaults to top)
   */
  const closeModal = useCallback((modalId) => {
    setSessionState(prev => {
      if (modalId) {
        return {
          ...prev,
          activeModals: prev.activeModals.filter(id => id !== modalId),
        };
      }
      return {
        ...prev,
        activeModals: prev.activeModals.slice(0, -1),
      };
    });
  }, []);

  /**
   * Check if a specific modal is open
   * @param {string} modalId - The modal identifier
   * @returns {boolean}
   */
  const isModalOpen = useCallback((modalId) => {
    return sessionState.activeModals.includes(modalId);
  }, [sessionState.activeModals]);

  return {
    // Core state
    frameId,
    sessionState,
    updateSessionState,
    clearFrameSession,

    // Beast Feast helpers
    resetCookingState,
    toggleIngredient,
    addDiceToPool,
    updateDicePool,

    // Witherwild helpers
    addWitherTokens,
    clearWitherTokens,
    togglePhase,
    updateDiseaseStage,

    // Modal helpers
    openModal,
    closeModal,
    isModalOpen,

    // Convenience accessors
    cookingStep: sessionState.cookingStep,
    selectedIngredients: sessionState.selectedIngredients,
    dicePool: sessionState.dicePool,
    matchedSets: sessionState.matchedSets,
    mealRating: sessionState.mealRating,
    isProcessing: sessionState.isProcessing,
  };
};

export default useFrameSession;
